import { BaseApiClient } from './BaseApiClient';
import type { Conversation, Message, QuickReply } from '@/types/Chat';
import type { Page } from '@/types/Page';

const API_BASE = '/chat';

class ChatService extends BaseApiClient {
    private static readonly HISTORY_PAGE_SIZE = 100

    async getConversations(): Promise<Conversation[]> {
        return this.get<Conversation[]>(`${API_BASE}/conversations`);
    }

    async getHistory(conversationId: string, page = 0, size = ChatService.HISTORY_PAGE_SIZE): Promise<Page<Message>> {
        return this.get<Page<Message>>(`${API_BASE}/history/${conversationId}`, {
            params: { page, size }
        });
    }

    /** Keyset pagination: beforeSeq'ten ESKİ mesajlar (yukarı kaydırma). Yalnız content+last anlamlı. */
    async getHistoryBefore(conversationId: string, beforeSeq: number,
        size = ChatService.HISTORY_PAGE_SIZE): Promise<Page<Message>> {
        return this.get<Page<Message>>(`${API_BASE}/history/${conversationId}`, {
            params: { beforeSeq, size }
        });
    }

    /** Gap-fill: afterSeq'ten YENİ mesajlar (reconnect'te kaçanlar). Yalnız content+last anlamlı. */
    async getHistoryAfter(conversationId: string, afterSeq: number,
        size = ChatService.HISTORY_PAGE_SIZE): Promise<Page<Message>> {
        return this.get<Page<Message>>(`${API_BASE}/history/${conversationId}`, {
            params: { afterSeq, size }
        });
    }

    async startConversation(participantId: string): Promise<Conversation> {
        return this.post<Conversation>(`${API_BASE}/conversation`, { participantId });
    }

    /** Yayıncı: yöneticisiyle havuzlu sohbeti getir/oluştur ("Yöneticinle Konuş"). */
    async managerSeat(): Promise<Conversation> {
        return this.post<Conversation>(`${API_BASE}/manager-seat`, {});
    }

    /** Yönetici/admin: Takım Gelen Kutusu — tüm yayıncı koltuk sohbetleri. */
    async managerSeats(): Promise<Conversation[]> {
        return this.get<Conversation[]>(`${API_BASE}/manager-seats`);
    }

    async createGroup(title: string, participantIds: string[]): Promise<Conversation> {
        return this.post<Conversation>(`${API_BASE}/group`, { title, participantIds });
    }

    async addMembers(conversationId: string, participantIds: string[]): Promise<Conversation> {
        return this.post<Conversation>(`${API_BASE}/group/${conversationId}/members`, { participantIds });
    }

    async searchUsers(query: string): Promise<any[]> {
        return this.get<any[]>(`${API_BASE}/users`, { params: { q: query } });
    }

    /** Kendi mesajını düzelt (yalnız gönderen). */
    async editMessage(messageId: string, content: string): Promise<Message> {
        return this.put<Message>(`${API_BASE}/messages/${messageId}`, { content });
    }

    /** Kendi mesajını geri al (yalnız gönderen) — yerinde "geri alındı" izi kalır. */
    async unsendMessage(messageId: string): Promise<Message> {
        return this.delete<Message>(`${API_BASE}/messages/${messageId}`);
    }

    async deleteConversation(conversationId: string): Promise<void> {
        await this.delete(`${API_BASE}/conversation/${conversationId}`);
    }

    // ── Hızlı yanıt şablonları ──
    async getQuickReplies(): Promise<QuickReply[]> {
        return this.get<QuickReply[]>(`${API_BASE}/quick-replies`);
    }

    async createQuickReply(title: string, body: string, shared: boolean, sortOrder = 0): Promise<QuickReply> {
        return this.post<QuickReply>(`${API_BASE}/quick-replies`, { title, body, shared, sortOrder });
    }

    async updateQuickReply(id: string, title: string, body: string, sortOrder = 0): Promise<QuickReply> {
        return this.put<QuickReply>(`${API_BASE}/quick-replies/${id}`, { title, body, sortOrder });
    }

    async deleteQuickReply(id: string): Promise<void> {
        await this.delete(`${API_BASE}/quick-replies/${id}`);
    }

    async uploadFile(file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        return this.post<any>(`${API_BASE}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    }
}

export const chatService = new ChatService();
